import { useState, useEffect } from "react"
import { FiInbox, FiEye } from 'react-icons/fi'
import { FaTrashAlt } from 'react-icons/fa'
import api from '../../utils/api'

import Button from "../../shared/components/Button/Button"
import Modal from '../../shared/modal/Modal'
import ConfirmDeleteModal from '../../shared/components/ConfirmDeleteModal/ConfirmDeleteModal'
import NewInvoice from './NewInvoice'

/* Hooks */
import useMyPermission from '../../hooks/useMyPermission'
import useFlashMessage from '../../hooks/useFlashMessage'
import TimestampToString from '../../shared/functions/TimestampToString'

import './Fiscal.css'

const STATUS_LABEL = {
    rascunho: 'Rascunho',
    em_fila: 'Em Fila',
    processando: 'Processando',
    autorizada: 'Autorizada',
    rejeitada: 'Rejeitada',
    cancelada: 'Cancelada',
    erro: 'Erro',
}

function Fiscal() {
    const [token] = useState(localStorage.getItem('token') || '')
    const { setFlashMessage } = useFlashMessage()
    const { timeStampToDDMMAAAA } = TimestampToString()

    const [invoices, setInvoices] = useState([])
    const [load, setLoad] = useState(false)
    const [detailsItem, setDetailsItem] = useState(null)
    const [cancelItem, setCancelItem] = useState(null)
    const [modalNewInvoice, setModalNewInvoice] = useState(false)

    /* ---------------------- Permission ------------------------- */
    const [myPermission, setMyPermission] = useState({ group: "", })
    const { checkPermission } = useMyPermission()
    const myPermissionView = checkPermission(myPermission, "fiscal_view")
    const myPermissionCreate = checkPermission(myPermission, "fiscal_create")
    const myPermissionDelete = checkPermission(myPermission, "fiscal_delete")

    const getAll = () => {
        api.get('/fiscal/invoice/getall', { headers: { Authorization: `Bearer ${JSON.parse(token)}` } }).then((response) => {
            setInvoices(response.data.invoices)
            setMyPermission(response.data.permission)
            setLoad(true)
        })
    }

    useEffect(() => {
        getAll()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token])

    async function cancelInvoice(id) {
        let msgType = 'success'
        const data = await api.patch(`/fiscal/invoice/cancel/${id}`, {}, {
            headers: { Authorization: `Bearer ${JSON.parse(token)}` },
        }).then((response) => {
            return response.data
        }).catch((err) => {
            msgType = 'error'
            return err.response.data
        })

        setFlashMessage(data.message, msgType)
        if (msgType !== 'error') {
            setCancelItem(null)
            getAll()
        }
    }

    if (!load) {
        return <div className="pg-loading">Carregando notas fiscais...</div>
    }

    return myPermissionView ? (
        <section className="pg-page">

            <div className="pg-sticky-top">
                <div className="pg-header">
                    <div className="pg-header-title">
                        <p className="pg-title">Notas Fiscais</p>
                        <span className="pg-title-accent" />
                    </div>
                    <div className="pg-header-row">
                        <div className="pg-header-actions">
                            {myPermissionCreate && (
                                <Button variant="new" onClick={() => setModalNewInvoice(true)}>Nova Nota Fiscal</Button>
                            )}
                        </div>
                        <div className="pg-header-right">
                            <span className="pg-count">
                                {invoices.length} {invoices.length === 1 ? 'nota' : 'notas'}
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            <div className="pg-list">
                {invoices.length > 0 ? (
                    invoices.map((item) => (
                        <div key={item._id} className='pg-item'>
                            <div className='pg-item-main'>
                                <div className='invoice-row-text'>
                                    <div className='invoice-row-header'>
                                        <span className={`invoice-type-badge invoice-type-badge-${item.type}`}>{item.type === 'nfe' ? 'NF-e' : 'CT-e'}</span>
                                        <span className='invoice-row-name'>{item.recipient?.name}</span>
                                    </div>
                                    <div className='invoice-row-links'>
                                        <span className='invoice-row-link-item'>Série {item.series} · Nº {item.number}</span>
                                        {item.createdAt && <span className='invoice-row-link-item'>{timeStampToDDMMAAAA(item.createdAt)}</span>}
                                        {item.totalValue && <span className='invoice-row-price'>R$ {item.totalValue}</span>}
                                    </div>
                                </div>
                            </div>

                            <div className="invoice-status-col">
                                <span className={`invoice-status-badge invoice-status-badge-${item.status}`}>{STATUS_LABEL[item.status] || item.status}</span>
                            </div>

                            <div className='shd-row-box-item-action'>
                                <FiEye className="invoice-icon-row-details" title="Ver Detalhes" onClick={() => setDetailsItem(item)} />
                                {myPermissionDelete && item.status !== 'cancelada' && (
                                    <FaTrashAlt className="shd-icon-row-trash" title="Cancelar" onClick={() => setCancelItem(item)} />
                                )}
                            </div>
                        </div>
                    ))
                ) : (
                    <div className="pg-empty">
                        <FiInbox size={32} className="pg-empty-icon" />
                        <p className="pg-empty-title">Nenhuma nota fiscal emitida</p>
                        <p className="pg-empty-subtitle">Clique em "Nova Nota Fiscal" para começar.</p>
                    </div>
                )}
            </div>

            <Modal
                modalStatus={modalNewInvoice}
                changeStatus={setModalNewInvoice}
                t={1}
                w={98}
                h={97}
                content={
                    <NewInvoice
                        btCancelAction={() => setModalNewInvoice(false)}
                        handleOnSuccess={() => { setModalNewInvoice(false); getAll() }}
                    />
                }
            />

            <Modal
                modalStatus={!!detailsItem}
                changeStatus={() => setDetailsItem(null)}
                w={70}
                content={
                    detailsItem && (
                        <div className="invoice-details">
                            <div className="invoice-details-header">
                                <p className="invoice-details-title">{detailsItem.type === 'nfe' ? 'NF-e' : 'CT-e'} — Série {detailsItem.series} / Nº {detailsItem.number}</p>
                                <span className="invoice-details-title-accent" />
                                <p className="invoice-details-subtitle">Destinatário: {detailsItem.recipient?.name}</p>
                            </div>
                            <div className="invoice-details-items">
                                {(detailsItem.items || []).map((it, idx) => (
                                    <div key={idx} className="invoice-details-item">
                                        <span className="invoice-details-item-desc">{it.description}</span>
                                        <span className="invoice-details-item-meta">NCM {it.ncm} · CFOP {it.cfop} · {it.quantity} {it.unit}</span>
                                        {it.unitValue && <span className="invoice-details-item-value">R$ {it.unitValue}</span>}
                                    </div>
                                ))}
                            </div>
                            <pre className="invoice-details-payload">{JSON.stringify(detailsItem.payloadSnapshot, null, 2)}</pre>
                        </div>
                    )
                }
            />

            <ConfirmDeleteModal
                modalStatus={!!cancelItem}
                changeStatus={() => setCancelItem(null)}
                onConfirm={() => cancelInvoice(cancelItem._id)}
                message="Deseja realmente cancelar essa nota fiscal?"
                itemName={cancelItem && `Série ${cancelItem.series} / Nº ${cancelItem.number}`}
            />

        </section>
    ) : (
        <div className="pg-empty">
            <p className="pg-empty-title">Sem permissão para visualizar notas fiscais</p>
        </div>
    )
}

export default Fiscal
